import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Cloud,
  Database,
  Mail,
  Calendar,
  FileText,
  MessageSquare,
  Shield,
  Settings,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Plus,
  Check,
  HardDrive,
  Users,
  Briefcase,
  BookOpen,
  Building2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import connectionService from '../services/connectionService';

interface DataType {
  id: string;
  name: string;
  description: string;
  icon: React.FC<any>;
  enabled: boolean;
  comingSoon?: boolean;
}

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.FC<any>;
  status: 'connected' | 'disconnected' | 'error';
  category: 'productivity' | 'communication' | 'storage' | 'security';
  lastSync?: string;
  dataTypes?: DataType[];
}

const Integrations: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isMicrosoft365Expanded, setIsMicrosoft365Expanded] = useState<boolean>(false);
  const [microsoftDataTypes, setMicrosoftDataTypes] = useState<{[key: string]: boolean}>({
    email: true,
    calendar: false,
    teams: false,
    onedrive: false,
    sharepoint: false,
    contacts: false,
    tasks: false,
    onenote: false
  });
  const [microsoftConnectionStatus, setMicrosoftConnectionStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected');
  const [loading, setLoading] = useState(false);

  // Load connection status on mount via connectionService
  useEffect(() => {
    const loadConnectionStatus = async () => {
      try {
        const connections = await connectionService.getConnections();
        const o365 = connections.find((c: any) => c.provider === 'o365');
        if (o365 && o365.status === 'active') {
          setMicrosoftConnectionStatus('connected');
        } else if (o365 && o365.status === 'error') {
          setMicrosoftConnectionStatus('error');
        }
      } catch (error) {
        console.error('Error loading connection status:', error);
      }
    };
    loadConnectionStatus();
  }, []);

  const handleMicrosoftConnect = () => {
    localStorage.setItem('niwt_microsoft_data_types', JSON.stringify(microsoftDataTypes));
    navigate('/azure-setup');
  };

  const handleDataTypeToggle = (dataType: string) => {
    setMicrosoftDataTypes(prev => ({
      ...prev,
      [dataType]: !prev[dataType]
    }));
  };

  const integrations: Integration[] = [
    {
      id: 'microsoft365',
      name: 'Microsoft 365',
      description: 'Outlook, OneDrive, Teams, and more',
      icon: Building2,
      status: microsoftConnectionStatus,
      category: 'productivity',
      dataTypes: [
        {
          id: 'email',
          name: 'Outlook Email',
          description: 'Email messages and attachments',
          icon: Mail,
          enabled: microsoftDataTypes.email
        },
        {
          id: 'calendar',
          name: 'Calendar',
          description: 'Events and meeting schedules',
          icon: Calendar,
          enabled: microsoftDataTypes.calendar,
        },
        {
          id: 'contacts',
          name: 'Contacts',
          description: 'Address book and contact details',
          icon: Users,
          enabled: microsoftDataTypes.contacts,
          comingSoon: true
        },
        {
          id: 'sharepoint',
          name: 'SharePoint',
          description: 'Team sites and shared documents',
          icon: FileText,
          enabled: microsoftDataTypes.sharepoint,
          comingSoon: true
        },
        {
          id: 'teams',
          name: 'Microsoft Teams',
          description: 'Chat messages and team channels',
          icon: MessageSquare,
          enabled: microsoftDataTypes.teams,
          comingSoon: true
        },
        {
          id: 'onedrive',
          name: 'OneDrive',
          description: 'Personal cloud storage files',
          icon: Cloud,
          enabled: microsoftDataTypes.onedrive,
          comingSoon: true
        },
        {
          id: 'tasks',
          name: 'Tasks & To-Do',
          description: 'Task lists and to-do items',
          icon: CheckCircle,
          enabled: microsoftDataTypes.tasks,
          comingSoon: true
        },
        {
          id: 'onenote',
          name: 'OneNote',
          description: 'Digital notebooks and notes',
          icon: BookOpen,
          enabled: microsoftDataTypes.onenote,
          comingSoon: true
        }
      ]
    }
  ];

  const filteredIntegrations = selectedCategory === 'all'
    ? integrations
    : integrations.filter(i => i.category === selectedCategory);

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'connected': return 'text-green-600 bg-green-50';
      case 'error': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'connected': return CheckCircle;
      case 'error': return AlertCircle;
      default: return null;
    }
  };

  const handleConnect = (integration: Integration) => {
    if (integration.id === 'microsoft365') {
      handleMicrosoftConnect();
    }
  };

  const handleDisconnect = async (integration: Integration) => {
    if (integration.id !== 'microsoft365') return;

    if (!window.confirm('Are you sure you want to disconnect Microsoft 365? This will disable all synced services.')) {
      return;
    }

    setLoading(true);
    try {
      await connectionService.disconnectO365();
      setMicrosoftConnectionStatus('disconnected');
      setMicrosoftDataTypes({
        email: false,
        calendar: false,
        teams: false,
        onedrive: false,
        sharepoint: false,
        contacts: false,
        tasks: false,
        onenote: false
      });
    } catch (error) {
      console.error('Error disconnecting Microsoft 365:', error);
      setMicrosoftConnectionStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const renderMicrosoft365Card = (integration: Integration) => {
    const Icon = integration.icon;
    const StatusIcon = getStatusIcon(integration.status);
    const selectedCount = Object.entries(microsoftDataTypes)
      .filter(([key, value]) => {
        const dataType = integration.dataTypes?.find(dt => dt.id === key);
        return value && !dataType?.comingSoon;
      }).length;

    return (
      <div key={integration.id} className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-all ${isMicrosoft365Expanded ? 'col-span-full' : ''}`}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-4 flex-1">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">{integration.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{integration.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap capitalize ${getStatusColor(integration.status)}`}>
                {StatusIcon && <StatusIcon className="w-3 h-3 mr-1" />}
                {integration.status}
              </span>
              <button
                onClick={() => setIsMicrosoft365Expanded(!isMicrosoft365Expanded)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={isMicrosoft365Expanded ? "Collapse connectors" : "Expand connectors"}
              >
                {isMicrosoft365Expanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Data Types Selection */}
          {isMicrosoft365Expanded && (
            <div className="mt-6 mb-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Select data to sync:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {integration.dataTypes?.map(dataType => {
                const DataIcon = dataType.icon;
                return (
                  <label
                    key={dataType.id}
                    className={`relative flex flex-col p-3 rounded-lg border transition-colors ${
                      dataType.comingSoon
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                        : dataType.enabled
                        ? 'border-blue-500 bg-blue-50 cursor-pointer hover:bg-blue-100'
                        : 'border-gray-200 cursor-pointer hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={dataType.enabled}
                      onChange={() => !dataType.comingSoon && handleDataTypeToggle(dataType.id)}
                      disabled={dataType.comingSoon}
                      className="sr-only"
                    />
                    {dataType.comingSoon && (
                      <div className="flex justify-center mb-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 whitespace-nowrap">
                          Coming Soon
                        </span>
                      </div>
                    )}
                    <div className="flex items-center flex-1 min-w-0">
                      <div className={`w-5 h-5 rounded border-2 mr-2 flex-shrink-0 flex items-center justify-center ${
                        dataType.comingSoon
                          ? 'bg-gray-200 border-gray-300'
                          : dataType.enabled
                          ? 'bg-blue-600 border-blue-600'
                          : 'bg-white border-gray-300'
                      }`}>
                        {dataType.enabled && !dataType.comingSoon && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <DataIcon className={`w-4 h-4 mr-2 flex-shrink-0 ${
                        dataType.comingSoon
                          ? 'text-gray-400'
                          : dataType.enabled
                          ? 'text-blue-600'
                          : 'text-gray-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm font-medium ${
                          dataType.comingSoon
                            ? 'text-gray-500'
                            : dataType.enabled
                            ? 'text-gray-900'
                            : 'text-gray-600'
                        }`}>
                          {dataType.name}
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">{dataType.description}</p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-sm text-gray-600 mt-3">
              {selectedCount} data {selectedCount === 1 ? 'type' : 'types'} selected for synchronization
            </p>
          </div>
          )}

          {integration.lastSync && (
            <p className="text-xs text-gray-500 mb-4">Last synced: {integration.lastSync}</p>
          )}

          <div className="flex space-x-2">
            {integration.status === 'disconnected' ? (
              <button
                onClick={() => handleConnect(integration)}
                disabled={selectedCount === 0}
                className={`flex-1 px-4 py-2.5 font-medium text-sm rounded-lg transition-colors ${
                  selectedCount > 0
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {selectedCount === 0 ? 'Select data types to connect' : `Connect Microsoft 365`}
              </button>
            ) : integration.status === 'connected' ? (
              <>
                <button
                  onClick={() => navigate('/employee-selection')}
                  className="flex-1 px-4 py-2.5 bg-niwt-primary text-white font-medium text-sm rounded-lg hover:bg-niwt-secondary transition-colors flex items-center justify-center"
                >
                  <Users className="w-4 h-4 mr-1.5" />
                  Select Employees
                </button>
                <button
                  onClick={() => handleDisconnect(integration)}
                  className="px-4 py-2.5 bg-red-50 text-red-600 font-medium text-sm rounded-lg hover:bg-red-100 transition-colors"
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={() => handleConnect(integration)}
                className="flex-1 px-4 py-2.5 bg-yellow-50 text-yellow-700 font-medium text-sm rounded-lg hover:bg-yellow-100 transition-colors"
              >
                Reconnect
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStandardCard = (integration: Integration) => {
    const Icon = integration.icon;

    return (
      <div key={integration.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <Icon className="w-8 h-8 text-niwt-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{integration.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{integration.description}</p>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 whitespace-nowrap">
              Coming Soon
            </span>
          </div>

          <div className="flex space-x-2">
            <button
              disabled
              className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-400 font-medium text-sm rounded-lg cursor-not-allowed"
            >
              Not Available Yet
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-libre">Integrations</h1>
        <p className="text-gray-600 mt-1">Connect your tools and services to enhance your Niwt experience</p>
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 gap-6">
        {integrations.map(integration => {
          if (integration.id === 'microsoft365') {
            return renderMicrosoft365Card(integration);
          }
          return renderStandardCard(integration);
        })}
      </div>

      {/* Help Section */}
      <div className="bg-purple-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Need a specific integration?</h3>
        <p className="text-sm text-gray-600 mb-4">
          We're constantly adding new integrations. If you need a specific service connected, let us know and we'll prioritize it.
        </p>
        <button className="inline-flex items-center text-niwt-primary hover:text-niwt-primary/80 font-medium text-sm">
          Request an integration
          <ExternalLink className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

export default Integrations;
